import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {ConnectButton} from '@rainbow-me/rainbowkit';
import {useAccount, useSwitchChain, useWaitForTransactionReceipt, useWriteContract} from 'wagmi';
import {type Address, formatUnits, parseUnits} from 'viem';
import {AppSidebar} from "@/components/app-sidebar";
import {Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,} from "@/components/breadcrumb";
import {Separator} from "@/components/separator";
import {SidebarInset, SidebarProvider, SidebarTrigger,} from "@/components/sidebar";
import {Button} from "@/components/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/card";
import {Input} from "@/components/input";
import {Label} from "@/components/label";
import {RiAlertLine, RiCheckLine, RiErrorWarningLine, RiInformationLine, RiLoader4Line, RiLockLine,} from "@remixicon/react";
import {calculateRewards, type StakingOption, stakingOptions} from "@/configs/staking.config";
import {LRS_STAKING_ABI, LRS_STAKING_CONTRACT} from "@/abis/lrs-staking";
import {larissaChain} from "@/configs/chain";
import {useStaking} from '@/providers/global-data.provider';
import {Cell, Pie, PieChart, ResponsiveContainer, Tooltip} from 'recharts';

export default function StakingPage() {
  const {isConnected, chain} = useAccount();
  const {switchChain} = useSwitchChain();

  // Transaction state for LRS
  const [isStaking, setIsStaking] = useState(false);
  const [withdrawingStakeId, setWithdrawingStakeId] = useState<bigint | null>(null);

  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Get staking options
  const lrsStaking = stakingOptions.find((opt: StakingOption) => opt.id === "lrs-staking")!;

  // Get all dynamic values from global provider
  const {
    totalValueLocked,
    circulatingSupply,
    lrsTokenBalance,
    rewardPoolBalance,
    totalStakeCount,
    currentStakeAmount,
    userStakes,
    rewardPerStake,
    calculatedAPY,
    refetchAll,
  } = useStaking();

  // Contract write hooks
  const {writeContract: stakeTokens, data: stakeData} = useWriteContract();
  const {writeContract: withdrawStake, data: withdrawData} = useWriteContract();

  // Wait for stake transaction
  const {isLoading: isStakePending, isSuccess: isStakeSuccess} = useWaitForTransactionReceipt({
    hash: stakeData,
  });

  // Wait for withdraw transaction
  const {isLoading: isWithdrawPending, isSuccess: isWithdrawSuccess} = useWaitForTransactionReceipt({
    hash: withdrawData,
  });

  // Effect: Handle stake success
  useEffect(() => {
    if (isStakeSuccess) {
      setIsStaking(false);
      setSuccessMessage("Staking successful! Your tokens have been staked.");

      // Refetch all relevant data
      refetchAll();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    }
  }, [isStakeSuccess, refetchAll]);

  // Effect: Handle withdraw success
  useEffect(() => {
    if (isWithdrawSuccess) {
      setWithdrawingStakeId(null);
      setSuccessMessage("Stake withdrawn successfully! Your LRS has been returned.");

      // Refetch all relevant data
      refetchAll();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    }
  }, [isWithdrawSuccess, refetchAll]);

  // ============ BLOCKCHAIN TRANSACTION HANDLERS ============

  // Handle LRS staking
  const handleStake = async () => {
    if (!currentStakeAmount) {
      setError("Unable to fetch stake amount from contract");
      return;
    }

    setError("");
    setSuccessMessage("");
    setIsStaking(true);

    try {
      // Check if on correct network
      if (chain?.id !== larissaChain.id) {
        switchChain({chainId: larissaChain.id});
      }

      // Use the current stake amount from the contract (already in wei)
      const amountBigInt = currentStakeAmount as bigint;

      // The stake function is payable and takes no arguments - send LRS as value
      stakeTokens({
        address: LRS_STAKING_CONTRACT as Address,
        abi: LRS_STAKING_ABI,
        functionName: 'stake',
        value: amountBigInt,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Staking failed");
      setIsStaking(false);
    }
  };

  // Handle LRS withdraw
  const handleWithdraw = async (stakeId: bigint) => {
    setError("");
    setSuccessMessage("");
    setWithdrawingStakeId(stakeId);

    try {
      // Check if on correct network
      if (chain?.id !== larissaChain.id) {
        switchChain({chainId: larissaChain.id});
      }

      withdrawStake({
        address: LRS_STAKING_CONTRACT as Address,
        abi: LRS_STAKING_ABI,
        functionName: 'withdraw',
        args: [stakeId],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Withdraw failed");
      setWithdrawingStakeId(null);
    }
  };

  // ============ END LRS TRANSACTION HANDLERS ============

  // Helper functions
  const formatBalance = (balance: bigint | undefined, decimals: number = 18, fixValue: number = 2) => {
    if (!balance) return "0";
    return parseFloat(formatUnits(balance, decimals)).toFixed(fixValue);
  };

  // Format number in thousands/millions format
  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US', { notation: 'compact'}).format(value);
  };

  return (
    <SidebarProvider>
      <AppSidebar/>
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1"/>
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block"/>
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/blockchain">Blockchain</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block"/>
                      <BreadcrumbItem>
                        <BreadcrumbPage>Staking</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton/>
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">Staking</h1>
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              {/* Wallet Connection Warning */}
              {!isConnected && (
                <Card className="bg-yellow-500/10 border border-yellow-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiAlertLine className="size-6 text-yellow-500 flex-shrink-0 mt-0.5"/>
                      <div>
                        <h3 className="text-lg font-semibold text-yellow-500 mb-2">Wallet Connection Required</h3>
                        <p className="text-sm text-yellow-500/80">
                          Please connect your wallet to access staking features. You need a connected wallet to stake tokens and claim rewards.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Error Message */}
              {error && (
                <Card className="bg-red-500/10 border border-red-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiErrorWarningLine className="size-6 text-red-500 flex-shrink-0 mt-0.5"/>
                      <div>
                        <h3 className="text-lg font-semibold text-red-500 mb-2">Transaction Failed</h3>
                        <p className="text-sm text-red-500/80">{error}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Success Message */}
              {successMessage && (
                <Card className="bg-green-500/10 border border-green-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiCheckLine className="size-6 text-green-500 flex-shrink-0 mt-0.5"/>
                      <div>
                        <h3 className="text-lg font-semibold text-green-500 mb-2">Success!</h3>
                        <p className="text-sm text-green-500/80">{successMessage}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
                  {/* First Column: Stats Cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="h-full bg-background/50 border border-border px-3">
                      <div className="flex flex-col h-full justify-center">
                        <div className="text-xs text-muted-foreground mb-1">Total Staked Amount</div>
                        <div className="flex items-center">
                          <div className="w-1 h-6 bg-[#FF8A3C] mr-2"></div>
                          <div className="text-sm font-bold leading-tight">
                            {totalValueLocked ? `${formatBalance(parseUnits(`${totalValueLocked}`, 18))} LRS` : "Loading..."}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="h-full bg-background/50 border border-border px-3">
                      <div className="flex flex-col h-full justify-center">
                        <div className="text-xs text-muted-foreground mb-1">Active Stakes Count</div>
                        <div className="flex items-center">
                          <div className="w-1 h-6 bg-[#FF8A3C] mr-2"></div>
                          <div className="text-sm font-bold leading-tight">
                            {totalStakeCount ? `${totalStakeCount}` : "0"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="h-full bg-background/50 border border-border px-3">
                      <div className="flex flex-col h-full justify-center">
                        <div className="text-xs text-muted-foreground mb-1">APY</div>
                        <div className="flex items-center">
                          <div className="w-1 h-6 bg-[#FF8A3C] mr-2"></div>
                          <div className="text-sm font-bold leading-tight">
                            {calculatedAPY > 0 ? `${calculatedAPY.toFixed(2)}%` : "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Second Column: Pie Chart */}
                  <div className="flex flex-col h-full bg-background/50 border border-border px-3">
                    {circulatingSupply > 0 && totalValueLocked !== undefined ? (() => {
                      const lockedValue = totalValueLocked;
                      const unlockedValue = Math.max(0, circulatingSupply - lockedValue);
                      const lockedPercentage = circulatingSupply > 0 ? (lockedValue / circulatingSupply) * 100 : 0;
                      const unlockedPercentage = circulatingSupply > 0 ? (unlockedValue / circulatingSupply) * 100 : 0;

                      const pieData = [
                        {name: 'Locked', value: lockedValue, percentage: lockedPercentage, fill: '#FF8A3C'},
                        {name: 'Unlocked', value: unlockedValue, percentage: unlockedPercentage, fill: '#64748b'}
                      ];

                      return (
                        <div className="flex-1 flex items-center justify-between">
                          <div className="mt-2 space-y-1.5 pt-2">
                            <div className="text-xs text-muted-foreground mb-2">Circulating Supply and Total Staked Amount</div>
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-[#FF8A3C]"></div>
                                <span className="text-muted-foreground">Total Staked</span>
                              </div>
                              <span className="font-semibold text-xs" style={{ color: '#FF8A3C' }}>
                                    {formatNumber(lockedValue)} LRS
                                  </span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-[#64748b]"></div>
                                <span className="text-muted-foreground">Circulating Supply</span>
                              </div>
                              <span className="font-semibold text-xs" style={{ color: '#64748b' }}>{formatNumber(circulatingSupply)} LRS</span>
                            </div>
                          </div>

                          <div className="" style={{height: '140px', width: '140px'}}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={pieData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={35}
                                  outerRadius={55}
                                  dataKey="value"
                                  paddingAngle={2}
                                >
                                  {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill}/>
                                  ))}
                                </Pie>
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (!active || !payload || !payload[0]) return null;
                                    const data = payload[0].payload;
                                    const value = data.value;
                                    const percentage = data.percentage || 0;
                                    const formattedValue = formatNumber(value);
                                    return (
                                      <div style={{
                                        fontWeight: 'bold', 
                                        fontSize: '12px',
                                        padding: '4px 8px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        boxShadow: 'none'
                                      }}>
                                        {formattedValue} LRS ({percentage.toFixed(2)}%)
                                      </div>
                                    );
                                  }}
                                  contentStyle={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    boxShadow: 'none',
                                    padding: 0,
                                  }}
                                  labelStyle={{ display: 'none' }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      );
                    })() : (
                      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                        Loading...
                      </div>
                    )}
                  </div>
                </div>

                <Card className="bg-sidebar border border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="h-12 w-12 rounded-full flex items-center justify-center font-bold shadow-md"
                          style={{
                            backgroundColor: `${lrsStaking.tokenColor}20`,
                            color: lrsStaking.tokenColor,
                          }}
                        >
                          {lrsStaking.tokenSymbol}
                        </div>
                        <div>
                          <CardTitle>{lrsStaking.tokenName} Staking</CardTitle>
                          <CardDescription>{lrsStaking.description}</CardDescription>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">APY</div>
                        <div className="text-2xl font-bold text-[#FF8A3C]">
                          {calculatedAPY > 0 ? `${calculatedAPY.toFixed(2)}%` : (lrsStaking?.apy ? `${lrsStaking.apy}%` : "N/A")}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Balance Display */}
                    {isConnected && (
                      <div className="p-4 bg-[#FF8A3C]/10 border border-[#FF8A3C]/30 rounded-lg">
                        <div className="text-sm text-muted-foreground mb-1">Your LRS Balance</div>
                        <div className="text-2xl font-bold">{formatBalance(lrsTokenBalance as bigint)} LRS</div>
                      </div>
                    )}

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-background rounded-lg border border-border">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Current Stake Amount</div>
                        <div className="font-semibold">
                          {currentStakeAmount ? formatBalance(currentStakeAmount as bigint) : '0'} {lrsStaking.tokenSymbol}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Reward Per Stake</div>
                        <div className="font-semibold text-[#FF8A3C]">{rewardPerStake.toFixed(6)} LRS</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Lock Period</div>
                        <div className="font-semibold">Flexible</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Rewards</div>
                        <div className="font-semibold">{lrsStaking.rewardDistribution}</div>
                      </div>
                    </div>

                    {/* Staking Form */}
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="lrs-amount">Amount to Stake (Fixed by Contract)</Label>
                        <div className="flex gap-2 mt-2">
                          <Input
                            id="lrs-amount"
                            type="text"
                            readOnly
                            value={currentStakeAmount ? formatBalance(currentStakeAmount as bigint) : '0'}
                            className="bg-muted cursor-not-allowed"
                          />
                          <span className="flex items-center px-3 text-sm text-muted-foreground">LRS</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          This is the fixed stake amount set by the contract
                        </p>
                      </div>

                      {/* Projected Rewards */}
                      {currentStakeAmount && currentStakeAmount > BigInt(0) && (
                        <div className="p-4 bg-[#FF8A3C]/10 border border-[#FF8A3C]/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <RiInformationLine className="size-4 text-[#FF8A3C]"/>
                            <span className="text-sm font-semibold text-[#FF8A3C]">
                                  {rewardPerStake > 0 ? 'Reward Per Stake' : 'Projected Weekly Rewards'}
                                </span>
                          </div>
                          <div className="text-2xl font-bold">
                            {rewardPerStake > 0
                              ? `${rewardPerStake.toFixed(6)} ${lrsStaking.rewardToken}`
                              : `${calculateRewards(Number(formatUnits(currentStakeAmount as bigint, 18)), lrsStaking).toFixed(2)} ${lrsStaking.rewardToken}`}
                          </div>
                          <div className="text-sm text-muted-foreground mt-2">
                            {rewardPerStake > 0
                              ? 'Based on current reward pool and total stakes'
                              : `Estimated based on ${lrsStaking.apy}% APY`}
                          </div>
                          {rewardPoolBalance !== undefined && totalStakeCount !== undefined && (
                            <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-[#FF8A3C]/20">
                              Pool: {formatBalance(rewardPoolBalance as bigint)} LRS |
                              Stakes: {totalStakeCount ? totalStakeCount.toString() : '0'}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="space-y-3">
                        {/* Stake Button */}
                        <Button
                          className="w-full bg-[#FF8A3C] text-white hover:bg-[#FF8A3C]/90"
                          onClick={handleStake}
                          disabled={
                            !isConnected ||
                            !currentStakeAmount ||
                            currentStakeAmount === BigInt(0) ||
                            !lrsTokenBalance ||
                            (lrsTokenBalance as bigint) === BigInt(0) ||
                            (currentStakeAmount && (lrsTokenBalance as bigint) < (currentStakeAmount as bigint)) ||
                            isStaking ||
                            isStakePending
                          }
                        >
                          {isStaking || isStakePending ? (
                            <>
                              <RiLoader4Line className="mr-2 size-4 animate-spin"/>
                              Staking...
                            </>
                          ) : (
                            <>
                              <RiLockLine className="mr-2 size-4"/>
                              Stake {currentStakeAmount ? formatBalance(currentStakeAmount as bigint) : '0'} {lrsStaking.tokenSymbol}
                            </>
                          )}
                        </Button>
                        {!isConnected ? (
                          <p className="text-sm text-yellow-500 text-center">
                            You need a connected wallet to stake tokens
                          </p>
                        ) : (
                          currentStakeAmount && (!lrsTokenBalance || (lrsTokenBalance as bigint) === BigInt(0) || (lrsTokenBalance as bigint) < (currentStakeAmount as bigint)) && (
                            <p className="text-sm text-red-500 text-center">
                              Insufficient balance. You need {formatBalance(currentStakeAmount as bigint)} LRS to stake.
                            </p>
                          )
                        )}
                      </div>
                    </div>

                    {/* Current Stake Info */}
                    {isConnected && (
                      <div className="pt-4 border-t border-border">
                        <h4 className="font-semibold mb-3">Your Staking Summary</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="p-4 bg-background rounded-lg border border-border">
                            <div className="text-xs text-muted-foreground mb-1">Total Staked</div>
                            <div className="text-xl font-bold text-[#FF8A3C]">
                              {userStakes.reduce((total, stake) => total + stake.stakeAmt, BigInt(0)) > 0
                                ? formatBalance(userStakes.reduce((total, stake) => total + stake.stakeAmt, BigInt(0)))
                                : '0'} {lrsStaking.tokenSymbol}
                            </div>
                          </div>
                          <div className="p-4 bg-background rounded-lg border border-border">
                            <div className="text-xs text-muted-foreground mb-1">Active Stakes</div>
                            <div className="text-xl font-bold text-green-500">
                              {userStakes.filter(stake => stake.stakeStatus === BigInt(1)).length}
                            </div>
                          </div>
                        </div>

                        {/* User Stakes List */}
                        {userStakes && Array.isArray(userStakes) && userStakes.length > 0 ? (
                          <div className="space-y-2">
                            <h5 className="text-sm font-semibold text-muted-foreground">Your Stakes</h5>
                            {userStakes.map((stake, index) => (
                              <div key={index} className="p-3 bg-background rounded-lg border border-border">
                                <div className="flex items-center justify-between gap-4">
                                  <div className="grid grid-cols-4 gap-4 text-sm flex-1">
                                    <div>
                                      <div className="text-xs text-muted-foreground">Stake ID</div>
                                      <div className="font-semibold">#{stake.stakeID.toString()}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-muted-foreground">Amount</div>
                                      <div className="font-semibold">{formatBalance(stake.stakeAmt)} LRS</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-muted-foreground">Staked On</div>
                                      <div className="font-semibold">
                                        {new Date(Number(stake.stakeStartTime) * 1000).toLocaleDateString()}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-muted-foreground">Status</div>
                                      <div className={`font-semibold ${stake.stakeStatus === BigInt(1) ? 'text-green-500' : 'text-gray-500'}`}>
                                        {stake.stakeStatus === BigInt(1) ? 'Active' : 'Withdrawn'}
                                      </div>
                                    </div>
                                  </div>
                                  {stake.stakeStatus === BigInt(1) && (
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleWithdraw(stake.stakeID)}
                                      disabled={!isConnected || (withdrawingStakeId === stake.stakeID && isWithdrawPending)}
                                    >
                                      {withdrawingStakeId === stake.stakeID && isWithdrawPending ? (
                                        <>
                                          <RiLoader4Line className="mr-2 size-4 animate-spin"/>
                                          Withdrawing...
                                        </>
                                      ) : (
                                        'Withdraw'
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-muted-foreground text-center py-4 bg-background rounded-lg border border-border">
                            No active stakes yet. Stake your LRS tokens to start earning rewards!
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

